import { useEffect, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, ShieldCheck, TrendingUp, MapPin, Info, Briefcase } from "lucide-react";
import EmployerSalaryBands from "@/components/app/EmployerSalaryBands";
import MarketHubCard from "@/components/app/employer/MarketHubCard";
import FeederDataSourcesPill from "@/components/app/employer/FeederDataSourcesPill";
import FirmLogo from "@/components/app/FirmLogo";
import FeederUniStrip from "@/components/app/FeederUniStrip";
import TalentInterestButton from "@/components/app/TalentInterestButton";
import { trackEvent } from "@/lib/analytics";

/**
 * EmployerDetail — mini page for a single graduate employer. Deep-
 * linked from every row on the EmployerProgressionCard.
 *
 * Question the page answers for a family: "If we want our child to
 * end up at [Firm X], which UK courses actually feed there — and
 * how do their grads' salaries progress?" We show the same LEO 3y →
 * 5y trajectory the ranking card uses, then list every UK degree
 * that named this employer, sorted by LEO5 desc.
 */
export default function EmployerDetail() {
  const { slug } = useParams();
  const [search] = useSearchParams();
  const country = search.get("country") || "UK";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  // StrictMode double-mount guard for analytics tracking.
  const _trackedSlug = useRef(null);

  useEffect(() => {
    setData(null);
    setErr(null);
    api
      .get(`/employers/${encodeURIComponent(slug)}?country=${country}`)
      .then((r) => {
        setData(r.data);
        // Track for Firm Portal Analytics tab. Referrer subject/degree
        // come from the ?ref_subject / ?ref_degree_id query set by the
        // degree-page employer chip.
        if (_trackedSlug.current !== slug) {
          _trackedSlug.current = slug;
          const refSubject = search.get("ref_subject") || null;
          const refDegreeId = search.get("ref_degree_id") || null;
          trackEvent("firm_employer_page_view", {
            firm_slug: slug,
            ...(refSubject ? { referrer_subject: refSubject } : {}),
            ...(refDegreeId ? { referrer_degree_id: refDegreeId } : {}),
          });
        }
      })
      .catch((e) => setErr(e?.response?.status === 404 ? "not-found" : "network"));
  }, [slug, country]);

  if (err === "not-found") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <Link to="/employers" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-[#4C1D95]" data-testid="employer-detail-back">
          <ArrowLeft className="h-3 w-3" /> All accelerator employers
        </Link>
        <h1 className="mt-6 font-editorial text-3xl">Employer not found</h1>
        <p className="mt-3 text-stone-600">
          We don't have any {country} degrees that list <span className="font-mono">{slug}</span>{" "}
          as a top graduate employer yet.
        </p>
      </div>
    );
  }
  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <p className="text-stone-500">Couldn't load employer detail.</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="h-6 w-40 bg-stone-100 rounded animate-pulse mb-4" />
        <div className="h-10 w-96 bg-stone-100 rounded animate-pulse mb-6" />
        <div className="h-4 w-full bg-stone-100 rounded animate-pulse mb-3" />
        <div className="h-4 w-3/4 bg-stone-100 rounded animate-pulse" />
      </div>
    );
  }

  const currencySymbol = data.currency === "USD" ? "$" : "£";
  const kfmt = (v) => `${currencySymbol}${Math.round(v / 1000)}k`;

  return (
    <div data-testid="employer-detail-page" className="max-w-5xl mx-auto px-6 py-10">
      <Link
        to="/employers"
        data-testid="employer-detail-back"
        className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-[#4C1D95]"
      >
        <ArrowLeft className="h-3 w-3" /> All accelerator employers
      </Link>

      {/* Hero */}
      <div className="mt-6 flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <div className="label-uppercase text-stone-600 flex items-center gap-1.5">
            <TrendingUp className="h-3 w-3 text-emerald-700" />
            Career-accelerator employer
          </div>
          <div className="mt-2 flex items-start gap-4">
            <FirmLogo
              slug={slug}
              name={data.employer}
              sector={data.sector}
              IconFallback={Briefcase}
              size={64}
            />
            <h1
              className="font-editorial text-4xl sm:text-5xl leading-tight"
              data-testid="employer-detail-name"
            >
              {data.employer}
            </h1>
          </div>
          <p className="mt-3 text-stone-600 max-w-xl leading-relaxed">
            {data.has_feeder_data === false ? (
              <>
                We track this firm's published salary bands directly.
                Feeder-course cohort earnings aren't mapped yet — we
                add ~10 firms' feeder mappings per week.
              </>
            ) : data.has_progression === false ? (
              <>
                Ranked here by <span className="font-medium">feeder-course density</span> —
                how many curated {data.country} degrees list this firm as a top employer.
                Central payroll data isn't published in {data.country}, so we lead with the
                honest breadth signal and the firm's own careers page. Curated regional hub
                blurb below.
              </>
            ) : (
              <>
                The firm's own published pay bands sit below. The
                smaller LEO cohort figure to the right is the median
                earnings of every graduate from feeder courses — a
                useful accelerator signal, but{" "}
                <span className="font-medium">not this firm's pay</span>
                {" "}(most of those grads aren't at {data.employer}).
              </>
            )}
          </p>
        </div>
        {data.has_feeder_data !== false && data.has_progression !== false && (
          <div
            className="shrink-0 flex items-center gap-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-2"
            data-testid="employer-detail-trajectory"
            title="LEO cohort median earnings — reflects the whole feeder-course cohort, not this firm's actual pay"
          >
            <div className="text-[9px] font-underground-sc tracking-widest text-stone-400 leading-tight max-w-[80px]">
              Feeder cohort · not firm pay
            </div>
            <div className="flex items-center gap-2 font-mono text-[13px]">
              <span className="text-stone-500 tabular-nums">{kfmt(data.leo3_p50_gbp)}</span>
              <span className="text-stone-300" aria-hidden>→</span>
              <span className="text-stone-700 tabular-nums">{kfmt(data.leo5_p50_gbp)}</span>
              <span
                className="text-emerald-700 tabular-nums font-semibold ml-1"
                data-testid="employer-detail-lift"
              >
                +{data.lift_pct}%
              </span>
            </div>
          </div>
        )}
        {data.has_progression === false && data.n_degrees > 0 && (
          <div
            className="shrink-0 flex items-center gap-3 rounded-md border border-[#4C1D95]/25 bg-[#F5F0FF] px-3 py-2"
            data-testid="employer-detail-density"
            title={`Appears in ${data.n_degrees} curated ${data.country} degrees · ${data.n_unis} unis`}
          >
            <div className="text-[9px] font-underground-sc tracking-widest text-[#4C1D95]/70 leading-tight max-w-[80px]">
              Feeder density · {data.country}
            </div>
            <div className="flex items-center gap-2 font-mono text-[13px] text-[#4C1D95]">
              <span className="tabular-nums font-semibold">×{data.n_degrees}</span>
              <span className="text-stone-400 uppercase tracking-wider text-[10px]">
                degrees · {data.n_unis} unis
              </span>
            </div>
          </div>
        )}
      </div>

      <Link
        to={`/how-to-get-into/${slug}`}
        data-testid="employer-detail-firm-guide-cta"
        className="mt-6 inline-flex items-center gap-2 rounded-md border border-[#4C1D95]/40 bg-[#4C1D95]/[0.04] px-4 py-2.5 text-[13px] font-medium text-[#4C1D95] hover:bg-[#4C1D95] hover:text-white transition"
      >
        <TrendingUp className="h-4 w-4" />
        Read the application guide — how to get into {data.employer}
      </Link>

      <div className="mt-2">
        <TalentInterestButton firmSlug={slug} firmName={data.employer} />
      </div>

      {/* Regional hub card — surfaced for the 6 continental tier-A
          markets (IE/DE/FR/IT/ES/PT) where we have curated firm-hubs
          data. Shows office location + staff count + native-language
          careers URL so an international user lands on the right
          grad-scheme intake without hunting. */}
      {data.hub && (
        <div
          data-testid="employer-detail-regional-hub"
          className="mt-6 rounded-lg border border-[#4C1D95]/25 bg-[#F5F0FF]/60 p-5"
        >
          <div className="flex items-start gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-underground-sc tracking-widest text-[#4C1D95]/80">
                Regional presence · {data.country}
              </div>
              <h3 className="mt-1 font-editorial text-xl leading-tight text-[#002147]">
                {data.hub_name || data.employer}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-stone-700">
                {data.hub.hub}
              </p>
              {data.hub.location && (
                <div className="mt-2 text-[11px] font-underground-sc tracking-wider text-stone-500">
                  📍 {data.hub.location}
                </div>
              )}
            </div>
            {data.hub.grad_url && (
              <a
                href={data.hub.grad_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="employer-detail-hub-grad-link"
                className="shrink-0 inline-flex items-center gap-2 rounded-md bg-[#4C1D95] text-white px-4 py-2.5 text-[13px] font-medium hover:bg-[#5B21B6] transition"
              >
                Graduate scheme
                <TrendingUp className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        </div>
      )}

      {/* Visual pipeline — top-5 feeder unis as a logo row so the
          UK/US uni → firm pathway is legible at a glance. */}
      <FeederUniStrip slug={slug} />

      {data.has_feeder_data === false ? (
        <>
          <EmployerSalaryBands slug={data.slug} employerName={data.employer} />
          <MarketHubCard firmName={data.employer} country={data.country} />
          <div
            data-testid="employer-detail-no-feeder"
            className="mt-6 rounded-md border border-stone-200 bg-stone-50 px-4 py-3 text-[12px] leading-relaxed text-stone-600 flex items-start gap-2.5"
          >
            <Info className="h-4 w-4 text-stone-500 shrink-0 mt-0.5" />
            <div>
              <span className="font-semibold text-stone-800">Feeder-course mapping coming soon.</span>{" "}
              We haven't yet linked <span className="font-medium">{data.employer}</span> to
              specific {data.country} undergraduate courses in our catalog. The
              salary bands above are the firm's own published figures — no
              cohort proxy needed.
            </div>
          </div>
        </>
      ) : (
        <>
      {/* Firm-published bands lead the page — this is the honest
          answer to "what does this firm pay?". The tiny cohort tile
          in the hero above already caveats the LEO figure. */}
      <EmployerSalaryBands slug={data.slug} employerName={data.employer} />
      <MarketHubCard firmName={data.employer} country={data.country} />

      {/* Feeder degrees */}
      <div className="mt-10">
        <div className="label-uppercase text-stone-600 mb-3">
          Feeder courses · {data.n_degrees} {data.country} {data.n_degrees === 1 ? "degree" : "degrees"}
        </div>

        <FeederDataSourcesPill sources={data.employer_edges_sources} />

        {/* Reverse Career Match — the family arrived here because
            they aspire to work at this employer. Sort the feeder
            list by 3y→5y salary lift instead of absolute LEO5 so the
            REAL accelerator courses (grads climb fastest here) rise
            to the top, not just the ones that hire well on day 1. */}
        {(() => {
          const withLift = data.degrees
            .filter((d) => d.leo3_median_gbp && d.leo5_median_gbp && d.leo5_median_gbp > d.leo3_median_gbp)
            .map((d) => ({
              ...d,
              lift_pct: Math.round(((d.leo5_median_gbp - d.leo3_median_gbp) / d.leo3_median_gbp) * 100),
            }))
            .sort((a, b) => b.lift_pct - a.lift_pct)
            .slice(0, 5);
          if (withLift.length === 0) return null;
          return (
            <div
              data-testid="employer-detail-top-accelerators"
              className="mb-4 rounded-lg border border-[#4C1D95]/20 bg-[#F5F0FF] p-4"
            >
              <div className="flex items-center gap-1.5 mb-2 text-[11px] font-underground-sc tracking-wider text-[#4C1D95]">
                <TrendingUp className="h-3 w-3" /> Top 5 accelerator courses for {data.employer}
              </div>
              <ol className="space-y-1.5">
                {withLift.map((f, i) => (
                  <li
                    key={f.degree_id}
                    data-testid={`employer-detail-accelerator-${i}`}
                    className="flex items-center gap-3 text-sm"
                  >
                    <span className="w-5 text-right text-[11px] font-mono text-[#4C1D95]/70 tabular-nums">{i + 1}</span>
                    <Link
                      to={`/degree/${f.degree_id}`}
                      className="flex-1 min-w-0 truncate text-stone-900 hover:text-[#4C1D95]"
                    >
                      <span className="font-medium">{f.title}</span>
                      <span className="text-stone-500"> · {f.university_name}</span>
                    </Link>
                    <span
                      data-testid={`employer-detail-accelerator-${i}-lift`}
                      className="font-mono text-sm font-semibold text-emerald-700 shrink-0"
                    >
                      +{f.lift_pct}%
                    </span>
                  </li>
                ))}
              </ol>
              <p className="mt-2 text-[11px] text-stone-600 leading-relaxed">
                Ranked by how fast the whole course's grads see their
                salary grow between year 3 and year 5 — the fastest-
                growing pipelines into <span className="font-medium">{data.employer}</span>.
              </p>
            </div>
          );
        })()}
        <ul className="divide-y divide-stone-100 rounded-lg border border-stone-200 bg-white overflow-hidden">
          {data.degrees.map((d, i) => (
            <li
              key={d.degree_id}
              data-testid={`employer-detail-feeder-${i}`}
              className="py-3 px-4 flex items-center gap-4 hover:bg-stone-50 transition-colors"
            >
              <span className="w-6 shrink-0 text-right text-[11px] font-mono text-stone-400 tabular-nums">
                {i + 1}
              </span>
              <Link
                to={`/degree/${d.degree_id}`}
                className="flex-1 min-w-0 group"
                data-testid={`employer-detail-feeder-${i}-link`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-editorial text-lg truncate text-stone-900 group-hover:text-[#4C1D95] transition-colors">
                    {d.title}
                  </span>
                  {d.ucas_course_code_live_verified && (
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-700 shrink-0" aria-label="UCAS verified" />
                  )}
                </div>
                <div className="mt-0.5 text-[11px] font-underground-sc tracking-wider text-stone-500 truncate">
                  {d.university_name}
                  {d.city && <span className="text-stone-400"> · <MapPin className="inline-block h-2.5 w-2.5" /> {d.city}</span>}
                  {d.qualification && <span className="text-stone-400"> · {d.qualification}</span>}
                </div>
              </Link>
              <div className="hidden sm:flex items-center gap-3 shrink-0 font-mono text-[11px] text-stone-500">
                {d.leo3_median_gbp && d.leo5_median_gbp ? (
                  <>
                    <span data-testid={`employer-detail-feeder-${i}-leo3`}>
                      {kfmt(d.leo3_median_gbp)}
                    </span>
                    <span aria-hidden className="text-stone-400">→</span>
                    <span
                      data-testid={`employer-detail-feeder-${i}-leo5`}
                      className="text-emerald-800 font-semibold"
                    >
                      {kfmt(d.leo5_median_gbp)}
                    </span>
                  </>
                ) : (
                  <span
                    data-testid={`employer-detail-feeder-${i}-density`}
                    className="text-[10px] uppercase tracking-wider text-stone-400"
                  >
                    feeder · {data.country}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6 flex items-start gap-2 text-[11px] text-stone-500 leading-relaxed">
        <Info className="h-3 w-3 shrink-0 mt-0.5" />
        <span>
          Method: courses shown here list <span className="font-medium">{data.employer}</span>{" "}
          as a top graduate employer in {data.country === "UK" ? "HESA / editorial" : "editorial"} data.
          Salary figures are cohort-wide {data.progression_source.toLowerCase()} —
          they measure how graduates of these feeder courses earn overall, not what{" "}
          <span className="font-medium">{data.employer}</span> itself pays.
        </span>
      </div>
        </>
      )}
    </div>
  );
}
